import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api          from '../../api/client';
import StatCard     from '../../components/StatCard';
import AlertsPanel  from '../../components/AlertsPanel';
import DeviceStatusTable from '../../components/DeviceStatusTable';

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border2)', borderRadius:8, padding:'10px 14px', fontSize:12 }}>
      <div style={{ color:'var(--text2)', marginBottom:4 }}>{label}</div>
      {payload.map(p=><div key={p.dataKey} style={{ color:p.color }}>{p.name}: <strong>{Number(p.value).toLocaleString('tr-TR')}</strong></div>)}
    </div>
  );
};

export default function AdminDashboard() {
  const [dash,    setDash]    = useState(null);
  const [quality, setQuality] = useState([]);
  const [alarms,  setAlarms]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, q, a] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/data-quality'),
        api.get('/admin/alerts'),
      ]);
      setDash(d.data.data);
      setQuality(q.data.data || []);
      setAlarms(a.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 60000); return ()=>clearInterval(iv); }, [load]);

  const fmt = n => Number(n||0).toLocaleString('tr-TR');
  const stats = dash?.stats || {};

  const handleRegenerateKey = async (device) => {
    if (!window.confirm(`${device.name} için API key yenilenecek. Cihaz .env güncellenmeli. Devam?`)) return;
    try {
      const { data } = await api.post(`/devices/${device.id}/regenerate-key`);
      alert(`Yeni API Key:\n${data.data.api_key}`);
      load();
    } catch (e) { alert('Hata: ' + (e.response?.data?.message || e.message)); }
  };

  return (
    <div>
      {/* Başlık */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div className="page-header" style={{ margin:0 }}>
          <h1>Admin Dashboard</h1>
          <p>Sistem geneli izleme</p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          {loading ? <span className="spinner" style={{width:13,height:13}} /> : '↻'} Yenile
        </button>
      </div>

      {/* Alarmlar */}
      <div style={{ marginBottom:20 }}>
        <AlertsPanel alarms={alarms?.alarms||[]} loading={loading && !alarms} />
      </div>

      {/* Stat kartlar */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:12, marginBottom:24 }}>
        <StatCard label="Toplam Müşteri" value={fmt(stats.total_customers)}    icon="🏢" color="var(--accent)"  loading={loading} size="lg" />
        <StatCard label="Toplam Cihaz"   value={fmt(stats.total_devices)}      icon="📡" color="var(--purple)" loading={loading} />
        <StatCard label="Çevrimiçi"      value={fmt(stats.online_devices)}     icon="🟢" color="var(--green)"  loading={loading} />
        <StatCard label="Çevrimdışı"     value={fmt(stats.offline_devices)}    icon="🔴" color="var(--red)"    loading={loading} />
        <StatCard label="Veri Yok"       value={fmt(stats.no_data_devices)}    icon="⚠️" color="var(--yellow)" loading={loading} />
        <StatCard label="Aktif Alarm"    value={fmt(stats.active_alarms)}      icon="🔔" color="var(--red)"    loading={loading} />
        <StatCard label="Bugün Toplam"   value={fmt(stats.today_total_events)} icon="📊" color="var(--yellow)" loading={loading} />
        <StatCard label="Bu Ay Toplam"   value={fmt(stats.this_month_total)}   icon="🗓️" color="var(--accent)"  loading={loading} />
      </div>

      {/* Tab seçimi */}
      <div style={{ display:'flex', gap:4, marginBottom:20, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r-sm)', padding:4, width:'fit-content' }}>
        {[
          { key:'overview',  label:'Genel Bakış' },
          { key:'devices',   label:'Cihaz Durumu' },
          { key:'alarms',    label:`Alarmlar ${alarms?.count > 0 ? `(${alarms.count})` : ''}` },
        ].map(t => (
          <button key={t.key} onClick={()=>setActiveTab(t.key)}
            style={{ padding:'7px 18px', borderRadius:6, border:'none', fontFamily:'var(--font-body)', fontSize:13.5, fontWeight:500, background:activeTab===t.key?'var(--accent)':'transparent', color:activeTab===t.key?'#fff':'var(--text2)', cursor:'pointer' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* GENEL BAKIŞ */}
      {activeTab === 'overview' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          {/* Müşteri bazlı grafik */}
          <div className="card">
            <h3 style={{ fontSize:15, marginBottom:14 }}>Müşteri Bazlı Bugünkü Durum</h3>
            {loading ? (
              <div style={{ textAlign:'center', padding:20, color:'var(--text3)' }}><span className="spinner" /></div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={dash?.customer_summary||[]} margin={{top:0,right:5,bottom:0,left:-20}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="name" tick={{fill:'var(--text3)',fontSize:9}} />
                    <YAxis tick={{fill:'var(--text3)',fontSize:10}} />
                    <Tooltip content={<Tip />} />
                    <Bar dataKey="today_in" fill="var(--accent)" radius={[3,3,0,0]} name="Bugün Giriş" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="table-wrap" style={{ marginTop:14 }}>
                  <table>
                    <thead><tr><th>Müşteri</th><th>Plan</th><th>Cihaz</th><th>Online</th><th>Alarm</th><th>Bugün</th></tr></thead>
                    <tbody>
                      {(dash?.customer_summary||[]).map(c => (
                        <tr key={c.id}>
                          <td><strong>{c.name}</strong></td>
                          <td><span className="badge badge-blue">{c.plan}</span></td>
                          <td>{c.dev_count}</td>
                          <td><span className={`badge ${c.online_count>0?'badge-green':'badge-red'}`}>{c.online_count}/{c.dev_count}</span></td>
                          <td>{c.alarm_count > 0 ? <span style={{ color:'var(--red)', fontWeight:700 }}>{c.alarm_count}</span> : <span style={{ color:'var(--text3)' }}>—</span>}</td>
                          <td><strong>{fmt(c.today_in)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* Son Events */}
          <div className="card">
            <h3 style={{ fontSize:15, marginBottom:14 }}>Son Veri Akışı</h3>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Zaman</th><th>Cihaz</th><th>Müşteri</th><th>Yön</th><th>Sayı</th></tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} style={{ textAlign:'center', padding:20, color:'var(--text3)' }}><span className="spinner" /></td></tr>
                  ) : (dash?.recent_events||[]).map((ev,i)=>(
                    <tr key={i}>
                      <td style={{ fontSize:11, color:'var(--text3)' }}>{new Date(ev.event_time).toLocaleTimeString('tr-TR')}</td>
                      <td style={{ fontSize:12 }}>{ev.device_name}</td>
                      <td style={{ fontSize:12, color:'var(--text2)' }}>{ev.customer_name}</td>
                      <td><span className={`badge ${ev.direction==='in'?'badge-green':'badge-blue'}`}>{ev.direction==='in'?'↑ Giriş':'↓ Çıkış'}</span></td>
                      <td><strong>{ev.count}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* CİHAZ DURUMU */}
      {activeTab === 'devices' && (
        <div className="card">
          <h3 style={{ fontSize:15, marginBottom:16 }}>Tüm Cihaz Durumları</h3>
          <DeviceStatusTable
            devices={quality}
            loading={loading}
            showCustomer
            onRegenerateKey={handleRegenerateKey}
          />
        </div>
      )}

      {/* ALARMLAR */}
      {activeTab === 'alarms' && (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:16 }}>
            <div className="card" style={{ padding:'14px 16px', borderTop:'3px solid var(--red)' }}>
              <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text3)', marginBottom:6 }}>Kritik</div>
              <div style={{ fontSize:28, fontFamily:'var(--font-display)', fontWeight:800, color:'var(--red)' }}>{alarms?.critical_count||0}</div>
            </div>
            <div className="card" style={{ padding:'14px 16px', borderTop:'3px solid var(--yellow)' }}>
              <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text3)', marginBottom:6 }}>Uyarı</div>
              <div style={{ fontSize:28, fontFamily:'var(--font-display)', fontWeight:800, color:'var(--yellow)' }}>{alarms?.warning_count||0}</div>
            </div>
            <div className="card" style={{ padding:'14px 16px', borderTop:'3px solid var(--green)' }}>
              <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text3)', marginBottom:6 }}>Sistem</div>
              <div style={{ fontSize:28, fontFamily:'var(--font-display)', fontWeight:800, color: alarms?.system_healthy?'var(--green)':'var(--red)' }}>
                {alarms?.system_healthy ? 'Normal' : 'Sorunlu'}
              </div>
            </div>
          </div>
          <div className="card">
            <h3 style={{ fontSize:15, marginBottom:14 }}>Aktif Alarmlar</h3>
            <AlertsPanel alarms={alarms?.alarms||[]} loading={loading && !alarms} showEmpty />
          </div>
          {/* Son 10 alarm (resolved dahil) */}
          <div className="card" style={{ marginTop:14 }}>
            <h3 style={{ fontSize:15, marginBottom:14 }}>Son Alarm Geçmişi</h3>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Tarih</th><th>Cihaz</th><th>Müşteri</th><th>Tür</th><th>Seviye</th><th>Durum</th></tr></thead>
                <tbody>
                  {(dash?.recent_alarms||[]).map((a,i)=>(
                    <tr key={i}>
                      <td style={{ fontSize:11, color:'var(--text3)' }}>{new Date(a.created_at).toLocaleString('tr-TR')}</td>
                      <td style={{ fontSize:12 }}><code style={{ fontSize:11 }}>{a.device_key}</code></td>
                      <td style={{ fontSize:12, color:'var(--text2)' }}>{a.customer_name}</td>
                      <td style={{ fontSize:12 }}>{a.alert_type}</td>
                      <td><span className={`badge ${a.level==='critical'?'badge-red':'badge-yellow'}`}>{a.level}</span></td>
                      <td><span className={`badge ${a.status==='active'?'badge-red':'badge-green'}`}>{a.status==='active'?'Aktif':'Çözüldü'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
